import React from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, Target, Award } from 'lucide-react';

const PurposeSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  const features = [
    {
      icon: <Lightbulb className="w-12 h-12 text-primary" />,
      title: "Meaningful Assessment",
      description: " Move beyond traditional letter grades to provide clear, descriptive feedback."
    },
    {
      icon: <Target className="w-12 h-12 text-primary" />,
      title: "DOK-Based Learning",
      description: "Reflect how deeply students engage with ideas."
    },
    {
      icon: <Award className="w-12 h-12 text-primary" />,
      title: "AI-Powered Insights",
      description: "Quickly create feedback that reflects student thinking using AI."
    }
  ];

  return (
    <section id="purpose" className="py-20 bg-cream">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-primary mb-4">
            Our Purpose
          </h2>
          <div className="max-w-5xl mx-auto space-y-8">
            <p className="text-xl text-primary/80 leading-relaxed">
              We understand that moving from traditional grades to Depth of Knowledge (DOK) and proficiency scales can be challenging. Gradely provides AI-powered support to record, track, and analyze student learning, along with other classroom tools, while keeping professional judgment at the center of the process.
            </p>
            
            {/* Founders Section */}
            <div className="bg-secondary/10 rounded-2xl p-8 border border-secondary/20">
              <h3 className="text-2xl font-semibold text-primary mb-6 text-center">Our Founding Story</h3>
              <p className="text-lg text-primary/80 leading-relaxed text-center">
                Gradely was founded by <span className="font-semibold text-primary">Nezan</span>, a senior internal auditor at Vancouver Coastal Health with expertise in systems development, and his brother <span className="font-semibold text-primary">Dostum</span>, a Grade 5 teacher at Iqra Islamic School with extensive experience in curriculum development. Together, they created Gradely with the goal of making assessment practical, clear, and accessible to educators everywhere.
              </p>
              <p className="text-lg text-primary/80 leading-relaxed text-center mt-4">
                From the outset, our focus has been on keeping Gradely simple, clear, and intuitive. The platform is designed by educators, for educators, and grounded in the realities of how assessment—an essential yet often overlooked part of pedagogy—takes place in the modern classroom.
              </p>
            </div>

            <p className="text-lg text-primary/80 leading-relaxed">
              We recognize both the advantages and the limitations of AI in education. Gradely is not intended to hand over assessment to artificial intelligence. Instead, our purpose is to help teachers integrate DOK and proficiency scale-based assessment into their teaching, using AI as a supportive tool in ways that are appropriate and genuinely beneficial.
            </p>

            {/* What We Offer Section */}
            <div className="bg-primary/5 rounded-2xl p-8 border border-primary/10">
              <h3 className="text-2xl font-semibold text-primary mb-6 text-center">What We Offer</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-secondary rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-primary/80"><span className="font-semibold text-primary">DOK-based assessment recording</span> – Record student progress and track it throughout the year with precision.</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-secondary rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-primary/80"><span className="font-semibold text-primary">AI-generated feedback</span> – Convert assessment data into clear, personalized comments that support student growth.</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-secondary rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-primary/80"><span className="font-semibold text-primary">Customizable curriculum tools</span> – Create unit plans, lesson plans, and DOK skills tailored to your students' needs.</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-secondary rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-primary/80"><span className="font-semibold text-primary">Student analytics</span> – View learning patterns, identify individual needs, and recognize student achievements.</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-secondary rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-primary/80"><span className="font-semibold text-primary">Teacher resources</span> – Create comprehensive unit plans, lesson plans, rubrics, and class activities aligned with the BC curriculum and updated assessment models.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="grid md:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="bg-secondary/20 backdrop-blur-sm rounded-2xl p-8 border border-secondary/30"
              variants={itemVariants}
            >
              <div className="mb-6">
                {feature.icon}
              </div>
              <h3 className="text-2xl font-semibold text-primary mb-4">
                {feature.title}
              </h3>
              <p className="text-primary/80">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default PurposeSection;
